import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  useAssignMemberToNecOfficeMutation,
  useGetNecOfficeDetailsQuery,
  useUpdateNecOfficeMutation,
} from "../api/necOfficeApi";

import RolePrivilegesEditor from "../../access/components/RolePrivilegesEditor";

type UiMsg = { type: "success" | "error"; text: string } | null;

function errMsg(err: any) {
  return err?.data?.message || err?.error || "Request failed";
}

function fullName(m: any) {
  return (
    [m?.surname, m?.firstname, m?.othernames].filter(Boolean).join(" ") || "—"
  );
}

export default function NecOfficeDetailsPage() {
  const { id } = useParams();
  const officeId = Number(id);

  const { data, isLoading, error, refetch } = useGetNecOfficeDetailsQuery(
    officeId,
    { skip: !officeId }
  );

  const [updateOffice, { isLoading: saving }] = useUpdateNecOfficeMutation();
  const [assignMember, { isLoading: assigning }] =
    useAssignMemberToNecOfficeMutation();

  const [msg, setMsg] = useState<UiMsg>(null);

  /** ---------------- Edit form ---------------- */
  const [edit, setEdit] = useState({
    name: "",
    description: "",
    rank: "",
  });

  const nameRef = useRef<HTMLInputElement | null>(null);

  /** ---------------- Assign form ---------------- */
  const [assignForm, setAssignForm] = useState({
    member_id: "",
    executive_set_id: "",
    position: "",
  });

  /** ---------------- Hydrate edit form ---------------- */
  useEffect(() => {
    if (!data) return;

    setEdit({
      name: data.name ?? "",
      description: data.description ?? "",
      rank: data.rank != null ? String(data.rank) : "",
    });
  }, [data]);

  /** ---------------- NECs ---------------- */
  const necs = useMemo(() => {
    return data?.necs ?? [];
  }, [data]);

  /** ---------------- Save office ---------------- */
  const onSave = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);

    if (!edit.name.trim()) {
      nameRef.current?.focus();
      return setMsg({ type: "error", text: "Office name is required" });
    }

    try {
      const res: any = await updateOffice({
        id: officeId,
        name: edit.name.trim(),
        description: edit.description?.trim() || null,
        rank: edit.rank ? Number(edit.rank) : null,
      }).unwrap();

      setMsg({
        type: "success",
        text: res?.message || "Office updated successfully",
      });

      refetch();
    } catch (err) {
      setMsg({ type: "error", text: errMsg(err) });
    }
  };

  /** ---------------- Assign member ---------------- */
  const onAssign = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);

    if (!assignForm.member_id.trim())
      return setMsg({ type: "error", text: "member_id is required" });

    if (!assignForm.executive_set_id.trim())
      return setMsg({ type: "error", text: "executive_set_id is required" });

    try {
      const res: any = await assignMember({
        member_id: assignForm.member_id.trim(),
        office_id: officeId,
        executive_set_id: Number(assignForm.executive_set_id),
        position: assignForm.position?.trim() || undefined,
      }).unwrap();

      setMsg({
        type: "success",
        text: res?.message || "Member assigned successfully",
      });

      setAssignForm({ member_id: "", executive_set_id: "", position: "" });
      refetch();
    } catch (err) {
      setMsg({ type: "error", text: errMsg(err) });
    }
  };

  const busy = saving || assigning;

  /** ================= RENDER ================= */
  return (
    <div className="bg-white border rounded-xl p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <Link
            to="/nec-offices"
            className="text-sm text-gray-600 hover:underline"
          >
            ← Back to NEC Offices
          </Link>

          <h1 className="text-xl font-bold text-gray-800 mt-2">
            NEC Office Details
          </h1>

          <p className="text-sm text-gray-500 mt-1">
            Manage office info, NEC members, and role privileges.
          </p>
        </div>

        <button
          onClick={() => refetch()}
          className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
        >
          Refresh
        </button>
      </div>

      {/* Feedback */}
      {msg && (
        <div
          className={`mt-4 rounded-lg border p-3 text-sm ${
            msg.type === "success"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          {msg.text}
        </div>
      )}

      {/* States */}
      {isLoading ? (
        <div className="mt-6 text-gray-600">Loading office…</div>
      ) : error ? (
        <div className="mt-6 text-red-600">Failed to load office.</div>
      ) : !data ? (
        <div className="mt-6 text-gray-600">Office not found.</div>
      ) : (
        <div className="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-4">
          {/* Office summary */}
          <div className="border rounded-xl p-4">
            <div className="font-semibold text-gray-800">Office Info</div>

            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="border rounded-lg p-3">
                <div className="text-xs text-gray-500">Name</div>
                <div className="font-semibold mt-1">{data.name}</div>
              </div>

              <div className="border rounded-lg p-3">
                <div className="text-xs text-gray-500">Slug</div>
                <div className="font-semibold mt-1">{data.slug ?? "—"}</div>
              </div>

              <div className="border rounded-lg p-3">
                <div className="text-xs text-gray-500">Rank</div>
                <div className="font-semibold mt-1">{data.rank ?? "—"}</div>
              </div>

              <div className="border rounded-lg p-3">
                <div className="text-xs text-gray-500">Role</div>
                <div className="font-semibold mt-1">
                  {data.role?.name ?? data.role_id ?? "—"}
                </div>
              </div>
            </div>

            {data.description && (
              <div className="mt-3 text-sm">
                <div className="text-xs text-gray-500">Description</div>
                <div className="mt-1">{data.description}</div>
              </div>
            )}
          </div>

          {/* Edit office */}
          <div className="border rounded-xl p-4">
            <div className="font-semibold text-gray-800">Edit Office</div>

            <form onSubmit={onSave} className="mt-3 space-y-3">
              <div>
                <label className="text-sm text-gray-600">Name</label>
                <input
                  ref={nameRef}
                  value={edit.name}
                  onChange={(e) =>
                    setEdit((p) => ({ ...p, name: e.target.value }))
                  }
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  disabled={busy}
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Description</label>
                <textarea
                  value={edit.description}
                  onChange={(e) =>
                    setEdit((p) => ({ ...p, description: e.target.value }))
                  }
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  rows={3}
                  disabled={busy}
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Rank</label>
                <input
                  type="number"
                  value={edit.rank}
                  onChange={(e) =>
                    setEdit((p) => ({ ...p, rank: e.target.value }))
                  }
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  disabled={busy}
                />
              </div>

              <div className="flex justify-end">
                <button
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm disabled:opacity-60"
                >
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>

          {/* NEC members */}
          <div className="border rounded-xl p-4 xl:col-span-2">
            <div className="font-semibold text-gray-800 mb-2">
              Assigned NEC Members ({necs.length})
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2">Member</th>
                    <th className="py-2">Membership No</th>
                    <th className="py-2">Position</th>
                    <th className="py-2">Executive Set</th>
                    <th className="py-2">Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {necs.map((n: any) => (
                    <tr key={n.id} className="border-b">
                      <td className="py-3 font-semibold">
                        {fullName(n.member)}
                      </td>
                      <td className="py-3">{n.member?.membership_no ?? "—"}</td>
                      <td className="py-3">{n.position ?? "—"}</td>
                      <td className="py-3">{n.executive_set?.name ?? "—"}</td>
                      <td className="py-3">
                        <div>{n.member?.email ?? "—"}</div>
                        <div className="text-xs text-gray-500">
                          {n.member?.phone ?? ""}
                        </div>
                      </td>
                    </tr>
                  ))}

                  {!necs.length && (
                    <tr>
                      <td colSpan={5} className="py-6 text-gray-500">
                        No NEC members assigned to this office yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Role privileges */}
          <div className="xl:col-span-2">
            {data.role_id ? (
              <RolePrivilegesEditor roleId={data.role_id} />
            ) : (
              <div className="border rounded-xl p-4 text-sm text-gray-600">
                This office has no role attached.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
